/**
 * A utility to track visibility of elements using IntersectionObserver API
 * This can be used to track when a form or specific fields become visible in the viewport.
 */
export default class VisibilityTracker {
  constructor(options = {}) {
    this.threshold = options.threshold || 0.25; // 25% visible by default
    this.observer = null;
    this.callbacks = new Map();
    this.visibilityStates = new Map();

    this.init();
  }

  init() {
    if (!window.IntersectionObserver) {
      console.warn(
        "IntersectionObserver not supported, falling back to immediate visibility",
      );
      return;
    }

    this.observer = new IntersectionObserver(
      this.handleIntersection.bind(this),
      {
        root: null,
        rootMargin: "0px",
        threshold: this.threshold,
      },
    );
  }

  /**
   * Track visibility of an element
   * @param {HTMLElement|string} elementOrSelector - Element or CSS selector to track
   * @param {Function} callback - Function to call when element becomes visible
   */
  track(elementOrSelector, callback) {
    const element =
      typeof elementOrSelector === "string"
        ? document.querySelector(elementOrSelector)
        : elementOrSelector;

    if (!element) {
      console.warn("VisibilityTracker: Element not found:", elementOrSelector);
      return;
    }

    // If no IntersectionObserver support, call callback immediately
    if (!this.observer) {
      this.executeCallback(element, callback);
      return;
    }

    this.callbacks.set(element, {
      callback,
      triggered: false,
    });

    this.observer.observe(element);
  }

  /**
   * Handle intersection observer entries
   */
  handleIntersection(entries) {
    entries.forEach((entry) => {
      const element = entry.target;
      const callbackData = this.callbacks.get(element);

      if (!callbackData) return;

      const isVisible =
        entry.isIntersecting && entry.intersectionRatio >= this.threshold;
      const wasVisible = this.visibilityStates.get(element);

      // Only trigger if visibility state changed and element became visible
      if (isVisible && !wasVisible && !callbackData.triggered) {
        this.executeCallback(element, callbackData.callback);

        callbackData.triggered = true;
      }

      this.visibilityStates.set(element, isVisible);
    });
  }

  /**
   * Execute the visibility callback
   */
  executeCallback(element, callback) {
    try {
      callback();
    } catch (error) {
      console.error("VisibilityTracker callback error:", error);
    }
  }

  /**
   * Check if element is immediately visible (for forms already in viewport)
   */
  static isElementVisible(element, threshold = 0.25) {
    if (!element) return false;

    const rect = element.getBoundingClientRect();
    const viewHeight = Math.max(
      document.documentElement.clientHeight,
      window.innerHeight,
    );
    const viewWidth = Math.max(
      document.documentElement.clientWidth,
      window.innerWidth,
    );

    // Check if element is in viewport
    const verticalVisible = rect.bottom > 0 && rect.top < viewHeight;
    const horizontalVisible = rect.right > 0 && rect.left < viewWidth;

    if (!verticalVisible || !horizontalVisible) return false;

    // Calculate intersection ratio
    const visibleHeight =
      Math.min(rect.bottom, viewHeight) - Math.max(rect.top, 0);
    const visibleWidth =
      Math.min(rect.right, viewWidth) - Math.max(rect.left, 0);
    const visibleArea = visibleHeight * visibleWidth;
    const totalArea = rect.height * rect.width;

    return totalArea > 0 && visibleArea / totalArea >= threshold;
  }

  /**
   * Cleanup observer and maps
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }

    this.callbacks.clear();
    this.visibilityStates.clear();
  }
}
